import { useState, useEffect, useCallback, useRef } from 'react';
import { API, graphqlOperation } from 'aws-amplify';
import { studyPlannerService, GOAL_TYPES, TOPIC_STATUS } from '../services/StudyPlannerService';
import { updateCandidateProfile } from '../graphql/mutations';
import { getCandidateProfile } from '../graphql/queries';

/**
 * Custom hook for Study Planner functionality
 * Manages WebSocket connection, state, and database synchronization
 * 
 * Storage Strategy:
 * - localStorage: sessionId for quick access
 * - GraphQL (candidateGoal): Goal type string
 * - GraphQL (candidateData): Full study data as JSON
 */

// WebSocket server URL - should be in environment config
const WS_SERVER_URL = 'wss://phone.getscreened.in/getscreenedbot/ws';

/**
 * Parse candidateData from GraphQL (stored as AWSJSON string)
 */
const parseCandidateData = (candidateDataStr) => {
  if (!candidateDataStr) return null;
  try {
    return typeof candidateDataStr === 'string' 
      ? JSON.parse(candidateDataStr) 
      : candidateDataStr;
  } catch (error) {
    console.error('[useStudyPlanner] Failed to parse candidateData:', error);
    return null;
  }
};

/**
 * Stringify data for GraphQL AWSJSON field
 */
const stringifyCandidateData = (data) => {
  try {
    return JSON.stringify(data);
  } catch (error) {
    console.error('[useStudyPlanner] Failed to stringify candidateData:', error);
    return null;
  }
};

export const useStudyPlanner = (candidateProfileId) => {
  // Connection state
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  
  // Session state
  const [sessionId, setSessionId] = useState(null);
  const [isSessionActive, setIsSessionActive] = useState(false);
  
  // Goal state
  const [goalData, setGoalData] = useState(null);
  const [goalType, setGoalType] = useState(null);
  
  // Roadmap state
  const [roadmap, setRoadmap] = useState(null);
  const [isGeneratingRoadmap, setIsGeneratingRoadmap] = useState(false);
  const [roadmapProgress, setRoadmapProgress] = useState(null);
  const [roadmapError, setRoadmapError] = useState(null);
  
  // Progress state
  const [topicProgress, setTopicProgress] = useState({});
  const [overallProgress, setOverallProgress] = useState({ completed: 0, total: 0, percentage: 0 });
  
  // Practice questions state
  const [practiceQuestions, setPracticeQuestions] = useState(null);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  
  // Resources state
  const [resources, setResources] = useState(null);
  const [isLoadingResources, setIsLoadingResources] = useState(false);
  
  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  // Error state
  const [error, setError] = useState(null);
  
  // Refs for cleanup
  const mountedRef = useRef(true);
  const candidateDataRef = useRef(null);

  // ============================================
  // Database Operations
  // ============================================

  /**
   * Load study planner data from database
   */
  const loadFromDatabase = useCallback(async () => {
    if (!candidateProfileId) return null;
    
    try {
      const response = await API.graphql(
        graphqlOperation(getCandidateProfile, { id: candidateProfileId })
      );
      
      const profile = response.data.getCandidateProfile;
      if (!profile) return null;
      
      const candidateData = parseCandidateData(profile.candidateData);
      candidateDataRef.current = candidateData;
      
      return {
        goalType: profile.candidateGoal,
        studyData: candidateData
      };
    } catch (error) {
      console.error('[useStudyPlanner] Failed to load from database:', error);
      return null;
    }
  }, [candidateProfileId]);

  /**
   * Save study planner data to database
   */
  const saveToDatabase = useCallback(async (goalTypeValue, studyData) => {
    if (!candidateProfileId) return false;
    
    setIsSaving(true);
    try {
      const input = {
        id: candidateProfileId,
        candidateGoal: goalTypeValue,
        candidateData: stringifyCandidateData(studyData)
      };
      
      await API.graphql(
        graphqlOperation(updateCandidateProfile, { input })
      );
      
      candidateDataRef.current = studyData;
      console.log('[useStudyPlanner] Saved to database');
      return true;
    } catch (error) {
      console.error('[useStudyPlanner] Failed to save to database:', error);
      setError('Failed to save progress');
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [candidateProfileId]);

  /**
   * Update specific fields in candidateData
   */
  const updateStudyData = useCallback(async (updates) => {
    const currentData = candidateDataRef.current || {};
    const newData = { ...currentData, ...updates, updatedAt: new Date().toISOString() };
    
    const success = await saveToDatabase(goalType, newData);
    if (success && mountedRef.current) {
      candidateDataRef.current = newData;
    }
    return success;
  }, [goalType, saveToDatabase]);

  // ============================================
  // WebSocket Event Handlers
  // ============================================

  useEffect(() => {
    const handleConnected = () => {
      if (mountedRef.current) {
        setIsConnected(true);
        setConnectionError(null);
      }
    };

    const handleDisconnected = () => {
      if (mountedRef.current) {
        setIsConnected(false);
      }
    };

    const handleError = (data) => {
      if (mountedRef.current) {
        setConnectionError(data.error);
        setError(data.error);
      }
    };

    const handleSessionStarted = (data) => {
      if (mountedRef.current) {
        setSessionId(data.sessionId);
        setIsSessionActive(true);
        localStorage.setItem('studyPlannerSessionId', data.sessionId);
      }
    };

    const handleRoadmapProgress = (data) => {
      if (mountedRef.current) {
        setRoadmapProgress(data.message);
      }
    };

    const handleRoadmapReady = async (data) => {
      if (mountedRef.current) {
        setRoadmap(data.roadmap);
        setIsGeneratingRoadmap(false);
        setRoadmapProgress(null);
        
        // Calculate initial progress
        if (data.roadmap?.study_topics) {
          const total = data.roadmap.study_topics.length;
          setOverallProgress({ completed: 0, total, percentage: 0 });
        }
        
        // Save to database
        await updateStudyData({
          roadmap: data.roadmap,
          roadmapGeneratedAt: new Date().toISOString()
        });
      }
    };

    const handleRoadmapLoaded = async (data) => {
      if (mountedRef.current) {
        setRoadmap(data.roadmap);
        setTopicProgress(data.progress || {});
        
        // Calculate progress
        if (data.roadmap?.study_topics) {
          const total = data.roadmap.study_topics.length;
          const completed = Object.values(data.progress || {})
            .filter(p => p.status === TOPIC_STATUS.COMPLETED).length;
          setOverallProgress({
            completed,
            total,
            percentage: total > 0 ? Math.round((completed / total) * 100) : 0
          });
        }
      }
    };

    const handleRoadmapNotFound = (data) => {
      if (mountedRef.current) {
        setRoadmapError(data.message);
        setIsGeneratingRoadmap(false);
      }
    };

    const handleQuestionsReady = (data) => {
      if (mountedRef.current) {
        setPracticeQuestions({
          skill: data.skill,
          questions: data.questions
        });
        setIsLoadingQuestions(false);
      }
    };

    const handleResourcesReady = (data) => {
      if (mountedRef.current) {
        setResources({
          topic: data.topic,
          type: data.resourceType,
          items: data.resources,
          summary: data.summary
        });
        setIsLoadingResources(false);
      }
    };

    const handleProgressUpdated = async (data) => {
      if (mountedRef.current) {
        setTopicProgress(prev => ({
          ...prev,
          [data.topicId]: { status: data.status }
        }));
        setOverallProgress(data.overallProgress);
        
        // Save progress to database
        await updateStudyData({
          progress: {
            ...candidateDataRef.current?.progress,
            [data.topicId]: { status: data.status, updatedAt: new Date().toISOString() }
          },
          overallProgress: data.overallProgress
        });
      }
    };

    const handleStudyPlannerError = (data) => {
      if (mountedRef.current) {
        setError(data.error);
        setIsGeneratingRoadmap(false);
        setIsLoadingQuestions(false);
        setIsLoadingResources(false);
      }
    };

    // Register event handlers
    studyPlannerService.on('connected', handleConnected);
    studyPlannerService.on('disconnected', handleDisconnected);
    studyPlannerService.on('error', handleError);
    studyPlannerService.on('sessionStarted', handleSessionStarted);
    studyPlannerService.on('roadmapProgress', handleRoadmapProgress);
    studyPlannerService.on('roadmapReady', handleRoadmapReady);
    studyPlannerService.on('roadmapLoaded', handleRoadmapLoaded);
    studyPlannerService.on('roadmapNotFound', handleRoadmapNotFound);
    studyPlannerService.on('questionsReady', handleQuestionsReady);
    studyPlannerService.on('resourcesReady', handleResourcesReady);
    studyPlannerService.on('progressUpdated', handleProgressUpdated);
    studyPlannerService.on('studyPlannerError', handleStudyPlannerError);

    // Cleanup
    return () => {
      studyPlannerService.off('connected', handleConnected);
      studyPlannerService.off('disconnected', handleDisconnected);
      studyPlannerService.off('error', handleError);
      studyPlannerService.off('sessionStarted', handleSessionStarted);
      studyPlannerService.off('roadmapProgress', handleRoadmapProgress);
      studyPlannerService.off('roadmapReady', handleRoadmapReady);
      studyPlannerService.off('roadmapLoaded', handleRoadmapLoaded);
      studyPlannerService.off('roadmapNotFound', handleRoadmapNotFound);
      studyPlannerService.off('questionsReady', handleQuestionsReady);
      studyPlannerService.off('resourcesReady', handleResourcesReady);
      studyPlannerService.off('progressUpdated', handleProgressUpdated);
      studyPlannerService.off('studyPlannerError', handleStudyPlannerError);
    };
  }, [updateStudyData]);

  // ============================================
  // Initialization
  // ============================================

  useEffect(() => {
    mountedRef.current = true;
    
    const initialize = async () => {
      setIsLoading(true);
      
      try {
        // Load existing data from database
        const savedData = await loadFromDatabase();
        
        if (savedData) {
          setGoalType(savedData.goalType);
          
          if (savedData.studyData) {
            setGoalData(savedData.studyData.goalDetails);
            setRoadmap(savedData.studyData.roadmap);
            setTopicProgress(savedData.studyData.progress || {});
            setOverallProgress(savedData.studyData.overallProgress || { completed: 0, total: 0, percentage: 0 });
            
            // Restore session ID if exists
            if (savedData.studyData.sessionId) {
              setSessionId(savedData.studyData.sessionId);
              studyPlannerService.setSessionId(savedData.studyData.sessionId);
            }
          }
        }
        
        // Also check localStorage for session
        const storedSessionId = localStorage.getItem('studyPlannerSessionId');
        if (storedSessionId && !sessionId) {
          setSessionId(storedSessionId);
          studyPlannerService.setSessionId(storedSessionId);
        }
        
      } catch (error) {
        console.error('[useStudyPlanner] Initialization error:', error);
        setError('Failed to load study data');
      } finally {
        if (mountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    if (candidateProfileId) {
      initialize();
    } else {
      setIsLoading(false);
    }

    return () => {
      mountedRef.current = false;
    };
  }, [candidateProfileId, loadFromDatabase]);

  // ============================================
  // Public API Methods
  // ============================================

  /**
   * Connect to WebSocket server
   */
  const connect = useCallback(async () => {
    try {
      await studyPlannerService.connect(WS_SERVER_URL);
      return true;
    } catch (error) {
      setConnectionError(error.message);
      return false;
    }
  }, []);

  /**
   * Disconnect from WebSocket server
   */
  const disconnect = useCallback(() => {
    studyPlannerService.disconnect();
    setIsConnected(false);
    setIsSessionActive(false);
  }, []);

  /**
   * Start a new study planner session with goal
   */
  const startSession = useCallback(async (candidateProfile, goal) => {
    setError(null);
    
    // Save goal to database first
    const studyData = {
      goalDetails: goal,
      createdAt: new Date().toISOString()
    };
    
    const saved = await saveToDatabase(goal.goalType, studyData);
    if (!saved) {
      throw new Error('Failed to save goal');
    }
    
    setGoalType(goal.goalType);
    setGoalData(goal);
    
    // Connect if not connected
    if (!studyPlannerService.isConnected()) {
      await connect();
    }
    
    // Start WebSocket session
    const newSessionId = studyPlannerService.startSession(candidateProfile, goal);
    
    // Update database with session ID
    await updateStudyData({ sessionId: newSessionId });
    
    return newSessionId;
  }, [connect, saveToDatabase, updateStudyData]);

  /**
   * Generate roadmap for current goal
   */
  const generateRoadmap = useCallback((preferences = {}) => {
    if (!isSessionActive) {
      setError('No active session. Please set a goal first.');
      return;
    }
    
    setIsGeneratingRoadmap(true);
    setRoadmapError(null);
    studyPlannerService.generateRoadmap(preferences);
  }, [isSessionActive]);

  /**
   * Get practice questions for a skill
   */
  const getPracticeQuestions = useCallback((skill, count = 5) => {
    setIsLoadingQuestions(true);
    setPracticeQuestions(null);
    studyPlannerService.getPracticeQuestions(skill, count);
  }, []);

  /**
   * Search for learning resources
   */
  const searchResources = useCallback((topic, resourceType = 'all') => {
    setIsLoadingResources(true);
    setResources(null);
    studyPlannerService.searchResources(topic, resourceType);
  }, []);

  /**
   * Update progress for a topic
   */
  const updateTopicProgress = useCallback((topicId, progressData) => {
    studyPlannerService.updateProgress(topicId, progressData);
  }, []);

  /**
   * Mark a topic as completed
   */
  const markTopicComplete = useCallback((topicId, hoursSpent = 0, notes = '') => {
    updateTopicProgress(topicId, {
      status: TOPIC_STATUS.COMPLETED,
      hoursSpent,
      notes,
      completionDate: new Date().toISOString(),
      confidenceLevel: 8
    });
  }, [updateTopicProgress]);

  /**
   * Mark a topic as in progress
   */
  const markTopicInProgress = useCallback((topicId) => {
    updateTopicProgress(topicId, {
      status: TOPIC_STATUS.IN_PROGRESS
    });
  }, [updateTopicProgress]);

  /**
   * Update goal settings
   */
  const updateGoal = useCallback(async (newGoal) => {
    const success = await saveToDatabase(newGoal.goalType, {
      ...candidateDataRef.current,
      goalDetails: newGoal,
      updatedAt: new Date().toISOString()
    });
    
    if (success) {
      setGoalType(newGoal.goalType);
      setGoalData(newGoal);
    }
    
    return success;
  }, [saveToDatabase]);

  /**
   * Clear all study planner data (reset)
   */
  const resetStudyPlanner = useCallback(async () => {
    const success = await saveToDatabase(null, null);
    
    if (success) {
      setGoalType(null);
      setGoalData(null);
      setRoadmap(null);
      setTopicProgress({});
      setOverallProgress({ completed: 0, total: 0, percentage: 0 });
      setSessionId(null);
      localStorage.removeItem('studyPlannerSessionId');
      studyPlannerService.disconnect();
    }
    
    return success;
  }, [saveToDatabase]);

  /**
   * Check if user has an existing goal
   */
  const hasGoal = Boolean(goalType);

  /**
   * Check if roadmap exists
   */
  const hasRoadmap = Boolean(roadmap);

  return {
    // Connection
    isConnected,
    connectionError,
    connect,
    disconnect,
    
    // Session
    sessionId,
    isSessionActive,
    startSession,
    
    // Goal
    goalType,
    goalData,
    hasGoal,
    updateGoal,
    
    // Roadmap
    roadmap,
    hasRoadmap,
    isGeneratingRoadmap,
    roadmapProgress,
    roadmapError,
    generateRoadmap,
    
    // Progress
    topicProgress,
    overallProgress,
    updateTopicProgress,
    markTopicComplete,
    markTopicInProgress,
    
    // Practice Questions
    practiceQuestions,
    isLoadingQuestions,
    getPracticeQuestions,
    
    // Resources
    resources,
    isLoadingResources,
    searchResources,
    
    // State
    isLoading,
    isSaving,
    error,
    
    // Actions
    resetStudyPlanner,
    
    // Constants (for convenience)
    GOAL_TYPES,
    TOPIC_STATUS
  };
};

export default useStudyPlanner;
